/**
 * KROS / HSV költségvetés → strukturált BOQ tételek.
 *
 * A `scripts/rozpocet/extract.mjs` oszlopkonvencióját követi (böngészőben):
 *   D=típus (D/K/M), E=kód, F=név, G=MJ, H=mennyiség, I=egységár, J=összeg, AU=szint
 */

import { foldDiacritics } from "@/lib/knowledge/expandQuery";
import { columnIndex, type Workbook, type WorkbookSheet } from "@/lib/xlsx/readWorkbookFromBuffer";

const COL = {
  pc: "C",
  typ: "D",
  kod: "E",
  popis: "F",
  mj: "G",
  mnozstvo: "H",
  jcena: "I",
  cena: "J",
  level: "AU"
} as const;

export type BoqExtractedItem = {
  sheet_name: string;
  section: string;
  code: string;
  name: string;
  unit: string;
  quantity: number | null;
  unit_price: number | null;
  total: number | null;
  row_index: number;
  item_type: "K" | "M";
  search_text: string;
};

function clean(raw: string | undefined): string {
  return String(raw ?? "")
    .replace(/\s+/g, " ")
    .trim();
}

function num(raw: string | undefined): number | null {
  if (raw === undefined || raw === "") return null;
  const normalized = String(raw).replace(/\s/g, "").replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

function cell(row: Map<string, string> | undefined, col: string): string {
  return clean(row?.get(col));
}

function findLabelValue(rows: WorkbookSheet["rows"], label: string): string {
  for (const [rowNum, cells] of rows) {
    for (const [col, value] of cells) {
      if (clean(value) !== label) continue;
      const after = [...cells.entries()]
        .filter(([c]) => columnIndex(c) > columnIndex(col))
        .sort((a, b) => columnIndex(a[0]) - columnIndex(b[0]));
      if (after.length > 0) return clean(after[0]![1]);
      const next = rows.get(rowNum + 1);
      if (next && next.size > 0) {
        const first = [...next.entries()].sort(
          (a, b) => columnIndex(a[0]) - columnIndex(b[0])
        )[0];
        if (first) return clean(first[1]);
      }
      return "";
    }
  }
  return "";
}

function splitCode(raw: string): { code: string; title: string } {
  const m = /^(\S+)\s*-\s*(.*)$/.exec(clean(raw));
  if (!m) return { code: clean(raw), title: "" };
  return { code: m[1]!, title: clean(m[2]) };
}

function buildSearchText(parts: string[]): string {
  return foldDiacritics(parts.filter(Boolean).join(" "));
}

/** Egy munkalap KROS-szerű tételei. */
function extractSheetItems(sheet: WorkbookSheet): BoqExtractedItem[] {
  const items: BoqExtractedItem[] = [];
  let groupTitle = "";
  let dielTitle = "";
  let rowHits = 0;

  for (const rowNum of [...sheet.rows.keys()].sort((a, b) => a - b)) {
    const cells = sheet.rows.get(rowNum);
    if (!cells) continue;
    const typ = cell(cells, COL.typ);
    const kod = cell(cells, COL.kod);
    const popis = cell(cells, COL.popis);

    if (typ === "D") {
      if (!kod && !popis) continue;
      const level = num(cells.get(COL.level)) ?? 0;
      if (level <= 0) {
        groupTitle = popis || kod;
        dielTitle = "";
      } else {
        dielTitle = popis || kod;
      }
      continue;
    }

    if (typ !== "K" && typ !== "M") continue;
    if (!kod && !popis) continue;

    rowHits += 1;
    const section = dielTitle || groupTitle;
    const quantity = num(cells.get(COL.mnozstvo));
    const unit_price = num(cells.get(COL.jcena));
    const total = num(cells.get(COL.cena));
    const unit = cell(cells, COL.mj);
    const name = popis || kod;

    items.push({
      sheet_name: sheet.name,
      section,
      code: kod,
      name,
      unit,
      quantity,
      unit_price,
      total,
      row_index: rowNum,
      item_type: typ as "K" | "M",
      search_text: buildSearchText([section, kod, name, unit, sheet.name])
    });
  }

  // Ha nincs KROS típusoszlop, ne adjunk vissza üres zajt — a hívó próbálhat mást.
  if (rowHits === 0) return [];
  return items;
}

/**
 * Heuristika nem-KROS táblákhoz: fejlécben mennyiség/MJ, majd adatsorok.
 * Csak akkor fut, ha a KROS ág üres.
 */
function extractGenericSheetItems(sheet: WorkbookSheet): BoqExtractedItem[] {
  const rowNums = [...sheet.rows.keys()].sort((a, b) => a - b);
  if (rowNums.length < 2) return [];

  let headerRow = -1;
  let cols: { name?: string; unit?: string; qty?: string; price?: string; total?: string; code?: string } =
    {};

  for (const rn of rowNums.slice(0, 40)) {
    const row = sheet.rows.get(rn);
    if (!row) continue;
    const entries = [...row.entries()];
    const folded = entries.map(([c, v]) => [c, foldDiacritics(v)] as const);
    const find = (...needles: string[]) =>
      folded.find(([, v]) => needles.some((n) => v.includes(n)))?.[0];

    const qtyCol = find("mnozstvo", "mennyiseg", "quantity", "qty");
    const nameCol = find("popis", "megnevezes", "nazov", "description", "name");
    const unitCol = find("mj", "m.j", "jednotka", "unit", "egyse");
    if (!qtyCol || !nameCol) continue;

    headerRow = rn;
    const priceCol = find("jcena", "egysegar", "unit price", "cena/j");
    const totalCol = find("cena", "osszeg", "összeg", "total", "spolu");
    const codeCol = find("kod", "kód", "code", "pc");
    cols = { name: nameCol, qty: qtyCol };
    if (unitCol) cols.unit = unitCol;
    if (priceCol) cols.price = priceCol;
    if (totalCol) cols.total = totalCol;
    if (codeCol) cols.code = codeCol;
    break;
  }

  if (headerRow < 0 || !cols.name || !cols.qty) return [];

  const items: BoqExtractedItem[] = [];
  let section = sheet.name;

  for (const rn of rowNums) {
    if (rn <= headerRow) continue;
    const row = sheet.rows.get(rn);
    if (!row) continue;
    const name = clean(row.get(cols.name));
    const quantity = num(row.get(cols.qty));
    if (!name || quantity === null) {
      // Szekció-sor: van szöveg, nincs mennyiség
      if (name && quantity === null && row.size <= 3) section = name;
      continue;
    }
    const code = cols.code ? clean(row.get(cols.code)) : "";
    const unit = cols.unit ? clean(row.get(cols.unit)) : "";
    const unit_price = cols.price ? num(row.get(cols.price)) : null;
    const total = cols.total ? num(row.get(cols.total)) : null;
    items.push({
      sheet_name: sheet.name,
      section,
      code,
      name,
      unit,
      quantity,
      unit_price,
      total,
      row_index: rn,
      item_type: "K",
      search_text: buildSearchText([section, code, name, unit, sheet.name])
    });
  }
  return items;
}

/** Teljes munkafüzet → BOQ tételek (KROS előnyben, egyébként generikus). */
export function extractBoqFromWorkbook(workbook: Workbook): BoqExtractedItem[] {
  const out: BoqExtractedItem[] = [];
  // Az első lap gyakran rekapituláció — a KROS extract a detail sheeteket dolgozza.
  // Ha KROS jelek vannak, ugyanúgy végigmegyünk minden lapon; a D/K/M nélküli lapok üresek.
  let usedKros = false;
  for (const sheet of workbook.sheets) {
    const kros = extractSheetItems(sheet);
    if (kros.length > 0) {
      usedKros = true;
      out.push(...kros);
    }
  }
  if (usedKros) return out;

  for (const sheet of workbook.sheets) {
    out.push(...extractGenericSheetItems(sheet));
  }
  return out;
}

/** Objekt / rész meta a keresőszöveghez (opcionális dúsítás). */
export function enrichBoqSearchWithSheetMeta(
  workbook: Workbook,
  items: BoqExtractedItem[]
): BoqExtractedItem[] {
  const metaBySheet = new Map<string, string>();
  for (const sheet of workbook.sheets) {
    const objekt = splitCode(findLabelValue(sheet.rows, "Objekt:"));
    const cast = splitCode(findLabelValue(sheet.rows, "Časť:") || findLabelValue(sheet.rows, "Cast:"));
    const bits = [objekt.title || objekt.code, cast.title || cast.code].filter(Boolean);
    if (bits.length) metaBySheet.set(sheet.name, bits.join(" "));
  }
  if (metaBySheet.size === 0) return items;
  return items.map((item) => {
    const meta = metaBySheet.get(item.sheet_name) ?? "";
    if (!meta) return item;
    return {
      ...item,
      search_text: buildSearchText([item.search_text, meta])
    };
  });
}
